import { useEffect } from "react";

type PageMeta = {
  title: string;
  description: string;
  /** BCP-47-ish locale for og:locale, e.g. "en_US" / "es_MX". */
  ogLocale?: string;
  /** Two-letter lang for <html lang>, e.g. "en" / "es". */
  lang?: string;
};

function swapAttr(el: Element | null, attr: string, value: string): string | null {
  if (!el) return null;
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return prev;
}

/**
 * Sets the document title + description + Open Graph tags for a public page
 * and restores the previous values on unmount (the static index.html values
 * keep serving crawlers; this swap is for humans and language switches).
 *
 * Only public pages should use this — AppLayout owns the title on
 * authenticated routes and the two never mount together.
 */
export function usePageMeta({ title, description, ogLocale, lang }: PageMeta): void {
  useEffect(() => {
    const head = document.head;
    const descEl = head.querySelector('meta[name="description"]');
    const ogTitleEl = head.querySelector('meta[property="og:title"]');
    const ogDescEl = head.querySelector('meta[property="og:description"]');
    const ogLocaleEl = head.querySelector('meta[property="og:locale"]');

    const prevTitle = document.title;
    const prevLang = document.documentElement.lang;
    document.title = title;
    if (lang) document.documentElement.lang = lang;

    const prevDesc = swapAttr(descEl, "content", description);
    const prevOgTitle = swapAttr(ogTitleEl, "content", title);
    const prevOgDesc = swapAttr(ogDescEl, "content", description);
    const prevOgLocale = ogLocale ? swapAttr(ogLocaleEl, "content", ogLocale) : null;

    return () => {
      document.title = prevTitle;
      if (lang) document.documentElement.lang = prevLang;
      if (prevDesc !== null) descEl?.setAttribute("content", prevDesc);
      if (prevOgTitle !== null) ogTitleEl?.setAttribute("content", prevOgTitle);
      if (prevOgDesc !== null) ogDescEl?.setAttribute("content", prevOgDesc);
      if (prevOgLocale !== null) ogLocaleEl?.setAttribute("content", prevOgLocale);
    };
  }, [title, description, ogLocale, lang]);
}
